<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import EmailColorPicker from '../ui/EmailColorPicker.vue'
import EmailLinkInput from '../ui/EmailLinkInput.vue'
import { LINK_DEFAULT_COLOR } from '../../utils/emailLink'
import type { FooterAlignment } from '../../types'
import type { Icons } from '@/components/base/BaseIcon.vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    editor?: Editor
    variables?: { code: string; label: string }[]
    colorPresets?: { color: string; label: string }[]
    hideAlign?: boolean
    blockAlign?: FooterAlignment
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const emit = defineEmits<{
  (e: 'insert-variable', code: string): void
  (e: 'update:blockAlign', align: FooterAlignment): void
}>()

const blockAlignOptions: { value: FooterAlignment; icon: Icons; titleKey: string }[] = [
  { value: 'left', icon: 'IconoirAlignLeftBox', titleKey: 'pages.email.emailTemplateCreatePage.toolbar.alignLeft' },
  {
    value: 'center',
    icon: 'IconoirAlignHorizontalCenters',
    titleKey: 'pages.email.emailTemplateCreatePage.toolbar.alignCenter'
  },
  { value: 'right', icon: 'IconoirAlignRightBox', titleKey: 'pages.email.emailTemplateCreatePage.toolbar.alignRight' }
]

const showColorPicker = ref(false)
const showHighlightPicker = ref(false)
const showFontPicker = ref(false)
const showFontSizePicker = ref(false)
const showLinkInput = ref(false)
const showVariablePicker = ref(false)
const linkUrl = ref('')

const fontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32]
const DEFAULT_FONT_SIZE = 16
const HEADING_FONT_SIZES: Record<number, number> = { 1: 32, 2: 24, 3: 20, 4: 18, 5: 16, 6: 14 }

const fonts = [
  {
    label: t('pages.email.emailTemplateCreatePage.panel.fontSystemRecommended'),
    value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' }
]

const currentFont = computed(() => {
  if (!props.editor) return 'Inter'
  const fontFamily = props.editor.getAttributes('textStyle').fontFamily
  const found = fonts.find((f) => f.value === fontFamily)
  return found?.label || 'Inter'
})

const currentColor = computed(() => {
  if (!props.editor) return '000000'
  const color = props.editor.getAttributes('textStyle').color || '#000000'
  return color.replace('#', '')
})

const currentHighlight = computed(() => {
  if (!props.editor) return ''
  const color = props.editor.getAttributes('highlight').color
  return color ? color.replace('#', '') : ''
})

const currentFontSize = computed<number>(() => {
  if (!props.editor) return DEFAULT_FONT_SIZE
  const raw = props.editor.getAttributes('textStyle').fontSize as string | undefined
  if (raw) {
    const parsed = parseInt(raw, 10)
    if (Number.isFinite(parsed)) return parsed
  }
  for (let i = 1; i <= 6; i++) {
    if (props.editor.isActive('heading', { level: i })) return HEADING_FONT_SIZES[i]
  }
  return DEFAULT_FONT_SIZE
})

const setFont = (font: string) => {
  if (!props.editor) return
  props.editor.chain().focus().setFontFamily(font).run()
  showFontPicker.value = false
}

const setFontSize = (size: number) => {
  if (!props.editor) return
  props.editor.chain().focus().setParagraph().setFontSize(`${size}px`).run()
  showFontSizePicker.value = false
}

const setColor = (color: string) => {
  if (!props.editor) return
  props.editor.chain().focus().setColor(`#${color}`).run()
}

const setHighlight = (color: string) => {
  if (!props.editor) return
  props.editor
    .chain()
    .focus()
    .setHighlight({ color: `#${color}` })
    .run()
}

const removeHighlight = () => {
  if (!props.editor) return
  props.editor.chain().focus().unsetHighlight().run()
  showHighlightPicker.value = false
}

const toggleBold = () => {
  if (!props.editor) return
  props.editor.chain().focus().toggleBold().run()
}

const toggleItalic = () => {
  if (!props.editor) return
  props.editor.chain().focus().toggleItalic().run()
}

const toggleUnderline = () => {
  if (!props.editor) return
  props.editor.chain().focus().toggleUnderline().run()
}

const toggleBulletList = () => {
  if (!props.editor) return
  props.editor.chain().focus().toggleBulletList().run()
}

const toggleOrderedList = () => {
  if (!props.editor) return
  props.editor.chain().focus().toggleOrderedList().run()
}

const setAlign = (align: 'left' | 'center' | 'right') => {
  if (!props.editor) return
  props.editor.chain().focus().setTextAlign(align).run()
}

const addLink = (editor: Editor, href: string) => {
  if (editor.isActive('link')) {
    editor.chain().focus().extendMarkRange('link').updateAttributes('link', { href }).run()
    return
  }
  editor.chain().focus().setLink({ href }).run()
}

const removeLink = (editor: Editor) => {
  const chain = editor.chain().focus().extendMarkRange('link').unsetLink().unsetUnderline()
  if (editor.getAttributes('textStyle').color === LINK_DEFAULT_COLOR) chain.unsetColor()
  chain.run()
}

const onLinkSubmit = (href: string) => {
  if (props.editor) {
    if (href) {
      addLink(props.editor, href)
    } else {
      removeLink(props.editor)
    }
  }
  showLinkInput.value = false
  linkUrl.value = ''
}

const openLinkInput = () => {
  if (showLinkInput.value) {
    showLinkInput.value = false
    linkUrl.value = ''
    return
  }
  linkUrl.value = props.editor?.getAttributes('link')?.href || ''
  showLinkInput.value = true
}

const onInsertVariable = (code: string) => {
  emit('insert-variable', code)
  showVariablePicker.value = false
}

const closeAllPickers = () => {
  showColorPicker.value = false
  showHighlightPicker.value = false
  showFontPicker.value = false
  showFontSizePicker.value = false
  showLinkInput.value = false
  showVariablePicker.value = false
}
</script>

<template>
  <div class="flex w-max flex-col border-x border-border bg-white" @click.stop>
    <div class="flex flex-nowrap items-center gap-0.5 px-3 py-2 min-h-[60px]">
      <div
        class="flex flex-nowrap items-center gap-0.5"
        :class="{ 'pointer-events-none select-none opacity-40': disabled }"
        :aria-disabled="disabled || undefined"
      >
        <template v-if="blockAlign !== undefined">
          <div class="flex items-center gap-px">
            <button
              v-for="opt in blockAlignOptions"
              :key="opt.value"
              type="button"
              class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
              :class="{ 'bg-stone-200 text-foreground': blockAlign === opt.value }"
              :title="t(opt.titleKey)"
              @click="$emit('update:blockAlign', opt.value)"
            >
              <BaseIcon :name="opt.icon" class="h-5 w-5" />
            </button>
          </div>

          <div class="mx-1.5 h-6 w-px bg-border" />
        </template>

        <div class="relative flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 min-w-15 items-center justify-between rounded border border-transparent px-2 py-1 text-muted-foreground transition-all hover:border-border hover:bg-accent hover:text-foreground"
            @click="showFontPicker = !showFontPicker"
          >
            <span class="text-sm font-medium">{{ currentFont }}</span>
            <svg class="ml-1 h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          <div
            v-if="showFontPicker"
            class="absolute left-0 top-full z-[60] mt-1 min-w-40 rounded-lg border border-border bg-white p-1 shadow-lg"
          >
            <button
              v-for="font in fonts"
              :key="font.value"
              type="button"
              class="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
              :class="{ 'bg-stone-200 font-medium': currentFont === font.label }"
              :style="{ fontFamily: font.value }"
              @click="setFont(font.value)"
            >
              {{ font.label }}
            </button>
          </div>
        </div>

        <div class="relative flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 min-w-[70px] items-center justify-between rounded border border-transparent px-2 py-1 text-muted-foreground transition-all hover:border-border hover:bg-accent hover:text-foreground"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.fontSize')"
            @click="showFontSizePicker = !showFontSizePicker"
          >
            <BaseIcon name="IconoirTextSize" class="h-5 w-5" />
            <span class="text-xs text-muted-foreground">{{ currentFontSize }}px</span>
          </button>
          <div
            v-if="showFontSizePicker"
            class="absolute left-0 top-full z-[60] mt-1 min-w-[90px] rounded-lg border border-border bg-white p-1 shadow-lg"
          >
            <button
              v-for="size in fontSizes"
              :key="size"
              type="button"
              class="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
              :class="{ 'bg-stone-200 font-medium': currentFontSize === size }"
              @click="setFontSize(size)"
            >
              <span>{{ size }}px</span>
            </button>
          </div>
        </div>

        <div class="mx-1.5 h-6 w-px bg-border" />

        <div class="flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('bold') }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.bold')"
            @click="toggleBold"
          >
            <BaseIcon name="IconoirBold" class="h-5 w-5" />
          </button>
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('italic') }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.italic')"
            @click="toggleItalic"
          >
            <BaseIcon name="IconoirItalic" class="h-5 w-5" />
          </button>
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('underline') }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.underline')"
            @click="toggleUnderline"
          >
            <BaseIcon name="IconoirUnderline" class="h-5 w-5" />
          </button>
        </div>

        <div class="mx-1.5 h-6 w-px bg-border" />

        <div class="relative flex items-center gap-px">
          <EmailColorPicker
            :editor="editor"
            type="highlight"
            :model-value="currentHighlight"
            :color-presets="colorPresets"
            @update:model-value="setHighlight"
            @clear="removeHighlight"
          />
        </div>

        <div class="relative flex items-center gap-px">
          <EmailColorPicker
            :editor="editor"
            type="color"
            :model-value="currentColor"
            :color-presets="colorPresets"
            @update:model-value="setColor"
          />
        </div>

        <div class="mx-1.5 h-6 w-px bg-border" />

        <div class="flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('bulletList') }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.bulletList')"
            @click="toggleBulletList"
          >
            <BaseIcon name="IconoirList" class="h-5 w-5" />
          </button>
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('orderedList') }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.orderedList')"
            @click="toggleOrderedList"
          >
            <BaseIcon name="IconoirNumberedListLeft" class="h-5 w-5" />
          </button>
        </div>

        <template v-if="!hideAlign">
          <div class="mx-1.5 h-6 w-px bg-border" />

          <div class="flex items-center gap-px">
            <button
              type="button"
              class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
              :class="{ 'bg-stone-200 text-foreground': editor?.isActive({ textAlign: 'left' }) }"
              :title="t('pages.email.emailTemplateCreatePage.toolbar.alignLeft')"
              @click="setAlign('left')"
            >
              <BaseIcon name="IconoirAlignLeft" class="h-5 w-5" />
            </button>
            <button
              type="button"
              class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
              :class="{ 'bg-stone-200 text-foreground': editor?.isActive({ textAlign: 'center' }) }"
              :title="t('pages.email.emailTemplateCreatePage.toolbar.alignCenter')"
              @click="setAlign('center')"
            >
              <BaseIcon name="IconoirAlignCenter" class="h-5 w-5" />
            </button>
            <button
              type="button"
              class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
              :class="{ 'bg-stone-200 text-foreground': editor?.isActive({ textAlign: 'right' }) }"
              :title="t('pages.email.emailTemplateCreatePage.toolbar.alignRight')"
              @click="setAlign('right')"
            >
              <BaseIcon name="IconoirAlignRight" class="h-5 w-5" />
            </button>
          </div>
        </template>

        <div class="flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': editor?.isActive('link') || showLinkInput }"
            :title="t('pages.email.emailTemplateCreatePage.toolbar.addLink')"
            @click="openLinkInput"
          >
            <BaseIcon name="HeroLinkIconOutline" class="h-5 w-5" />
          </button>
        </div>
      </div>

      <template v-if="variables?.length">
        <div class="mx-1.5 h-6 w-px bg-border" />

        <div class="relative flex items-center gap-px">
          <button
            type="button"
            class="flex min-h-8 items-center justify-center rounded px-2 py-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
            :class="{ 'bg-stone-200 text-foreground': showVariablePicker }"
            :title="t('pages.email.emailTemplateCreatePage.insertVariable')"
            @click="showVariablePicker = !showVariablePicker"
          >
            <BaseIcon name="IconoirCodeBrackets" class="h-5 w-5" />
          </button>
          <div
            v-if="showVariablePicker"
            class="absolute right-0 top-full z-[60] mt-1 min-w-44 rounded-lg border border-border bg-white p-1 shadow-lg"
          >
            <button
              v-for="variable in variables"
              :key="variable.code"
              type="button"
              class="flex w-full items-center rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
              @click="onInsertVariable(variable.code)"
            >
              {{ variable.label }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <Transition
      enter-active-class="overflow-hidden transition-all duration-200 ease-out"
      enter-from-class="max-h-0 opacity-0 -translate-y-1"
      enter-to-class="max-h-32 opacity-100 translate-y-0"
      leave-active-class="overflow-hidden transition-all duration-150 ease-in"
      leave-from-class="max-h-32 opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div v-if="showLinkInput" class="border-t border-border bg-muted px-3 py-2">
        <EmailLinkInput v-model="linkUrl" autofocus @submit="onLinkSubmit" />
      </div>
    </Transition>
  </div>

  <div
    v-if="showColorPicker || showHighlightPicker || showFontPicker || showFontSizePicker || showVariablePicker"
    class="fixed inset-0 z-40"
    @click="closeAllPickers"
  />
</template>
